import { useState, useRef, useEffect } from 'react';

import { suggestEmailCorrection } from '../utils/emailTypo';
import { trackEvent } from '../utils/analytics';

export const EMAIL_TYPO_SUGGESTION_DELAY = 600;

/**
 * Состояние подсказки об опечатке в домене email.
 *
 * scheduleCheck — проверка в паузе набора, чтобы подсказка не мигала
 * на каждом символе, пока пользователь дописывает домен;
 * check — немедленная проверка (потеря фокуса);
 * accept — принятие подсказки, возвращает исправленный адрес.
 */
export function useEmailTypoSuggestion({ enabled = true, delay = EMAIL_TYPO_SUGGESTION_DELAY } = {}) {
  const [suggestion, setSuggestion] = useState(null);
  // показанная подсказка хранится в ref, а не в state: проверка может
  // прийти из отложенного таймера с уже устаревшим замыканием
  const shownRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const reset = () => {
    clearTimeout(timerRef.current);
    shownRef.current = null;
    setSuggestion(null);
  };

  const check = (value) => {
    clearTimeout(timerRef.current);
    if (!enabled) return;

    const found = suggestEmailCorrection(value);

    if (!found) {
      shownRef.current = null;
      setSuggestion(null);
      return;
    }

    if (shownRef.current === found.email) return;

    shownRef.current = found.email;
    setSuggestion(found);
    trackEvent('signup_email_typo_shown', {
      typo_domain: found.originalDomain,
      suggested_domain: found.domain,
    });
  };

  const scheduleCheck = (value) => {
    clearTimeout(timerRef.current);
    if (!enabled) return;
    timerRef.current = setTimeout(() => check(value), delay);
  };

  const accept = () => {
    if (!suggestion) return null;

    trackEvent('signup_email_typo_accepted', {
      typo_domain: suggestion.originalDomain,
      suggested_domain: suggestion.domain,
    });

    const { email } = suggestion;
    reset();
    return email;
  };

  return { suggestion, check, scheduleCheck, accept, reset };
}
